// Preset parameter mappings for Basic and Intermediate screener modes

// Basic Mode Strategy Mappings - 6 Core Strategies with Clear Parameters
export const basicStrategies = {
  // Strategy 1: Short-term ITM Call
  'short-itm-call': {
    days_until_exp: 60,  // Midpoint of 1-3 months (30-90 days)
    days_to_gain: 30,    // 50% of 60 days
    strike_pct: -0.25,   // -25% strike (ITM for calls)
    stock_gain_pct: 0.20, // +20% underlying move
    option_type: 'call',
    allocation: 1000,
    label: 'Short ITM Bull',
    tooltip: '1-3 month ITM call expecting 20% upside. Higher probability with leverage.',
    // Display values for UI
    display: {
      expiration: '1-3 months',
      strike: 'ITM -25%',
      daysToTarget: '50%',
      expectedMove: '+20%'
    }
  },
  // Strategy 2: Short-term OTM Call
  'short-otm-call': {
    days_until_exp: 60,  // Midpoint of 1-3 months
    days_to_gain: 30,    // 50% of 60 days
    strike_pct: 0.25,    // +25% strike (OTM for calls)
    stock_gain_pct: 0.20, // +20% underlying move
    option_type: 'call',
    allocation: 1000,
    label: 'Short OTM Bull',
    tooltip: '1-3 month OTM call for aggressive upside. Lower cost, higher risk.',
    display: {
      expiration: '1-3 months',
      strike: 'OTM +25%',
      daysToTarget: '50%',
      expectedMove: '+20%'
    }
  },
  // Strategy 3: Medium-term ITM Call
  'medium-itm-call': {
    days_until_exp: 255,  // Midpoint of 6-11 months (180-335 days)
    days_to_gain: 77,     // 30% of 255 days
    strike_pct: -0.25,    // -25% strike (ITM for calls)
    stock_gain_pct: 0.20,  // +20% underlying move
    option_type: 'call',
    allocation: 1000,
    label: 'Medium ITM Bull',
    tooltip: '6-11 month ITM call with time for thesis to develop. Balanced risk/reward.',
    display: {
      expiration: '6-11 months',
      strike: 'ITM -25%',
      daysToTarget: '30%',
      expectedMove: '+20%'
    }
  },
  // Strategy 4: Medium-term OTM Call
  'medium-otm-call': {
    days_until_exp: 255,  // Midpoint of 6-11 months
    days_to_gain: 77,     // 30% of 255 days
    strike_pct: 0.25,     // +25% strike (OTM for calls)
    stock_gain_pct: 0.20,  // +20% underlying move
    option_type: 'call',
    allocation: 1000,
    label: 'Medium OTM Bull',
    tooltip: '6-11 month OTM call for leveraged gains. More time, lower theta decay.',
    display: {
      expiration: '6-11 months',
      strike: 'OTM +25%',
      daysToTarget: '30%',
      expectedMove: '+20%'
    }
  },
  // Strategy 5: Long-term ITM Call (LEAPS)
  'long-itm-call': {
    days_until_exp: 365,  // 12+ months
    days_to_gain: 110,    // 30% of 365 days
    strike_pct: -0.25,    // -25% strike (ITM for calls)
    stock_gain_pct: 0.20,  // +20% underlying move
    option_type: 'call',
    allocation: 1000,
    label: 'LEAPS ITM Bull',
    tooltip: '12+ month ITM LEAP for long-term growth. Stock replacement strategy.',
    display: {
      expiration: '12+ months',
      strike: 'ITM -25%',
      daysToTarget: '30%',
      expectedMove: '+20%'
    }
  },
  // Strategy 6: Long-term OTM Call (LEAPS)
  'long-otm-call': {
    days_until_exp: 365,  // 12+ months
    days_to_gain: 110,    // 30% of 365 days
    strike_pct: 0.25,     // +25% strike (OTM for calls)
    stock_gain_pct: 0.20,  // +20% underlying move
    option_type: 'call',
    allocation: 1000,
    label: 'LEAPS OTM Bull',
    tooltip: '12+ month OTM LEAP for multi-year plays. Maximum leverage, minimal decay.',
    display: {
      expiration: '12+ months',
      strike: 'OTM +25%',
      daysToTarget: '30%',
      expectedMove: '+20%'
    }
  }
};

// Stock Movement Presets with descriptions - Split into positive and negative
export const stockMovements = {
  'p10': {
    value: 0.10,
    label: '+10%',
    tooltip: 'Conservative bullish estimate. Stock expected to rise 10%. Typical for stable large-cap growth.'
  },
  'p25': {
    value: 0.25,
    label: '+25%',
    tooltip: 'Moderate bullish estimate. Stock expected to rise 25%. Good for growth stocks with strong catalysts.'
  },
  'p50': {
    value: 0.50,
    label: '+50%',
    tooltip: 'Aggressive bullish estimate. Stock expected to rise 50%. For high-conviction, high-volatility plays.'
  },
  'n10': {
    value: -0.10,
    label: '-10%',
    tooltip: 'Conservative bearish estimate. Stock expected to fall 10%. Typical for defensive or hedging positions.'
  },
  'n25': {
    value: -0.25,
    label: '-25%',
    tooltip: 'Moderate bearish estimate. Stock expected to fall 25%. Good for weak fundamentals or market downturns.'
  },
  'n50': {
    value: -0.50,
    label: '-50%',
    tooltip: 'Aggressive bearish estimate. Stock expected to fall 50%. For high-conviction bearish plays or crashes.'
  }
};

// Intermediate Mode Preset Options - Card-based selection for each parameter category
export const intermediatePresets = {
  // Expiration Window presets
  expiration: {
    'ultra-short': {
      days_until_exp: 21,
      label: 'Ultra Short-Term',
      description: '< 1 month',
      tooltip: 'Very short-dated options with high theta decay. Best for quick momentum plays or earnings events.'
    },
    'short': {
      days_until_exp: 60,
      label: 'Short-Term',
      description: '1-3 months',
      tooltip: 'Short-term options balancing premium cost and time value. Good for swing trades.'
    },
    'medium': {
      days_until_exp: 135,
      label: 'Medium-Term',
      description: '3-6 months',
      tooltip: 'Medium-term options with reasonable theta decay. Allows time for thesis to develop.'
    },
    'long': {
      days_until_exp: 270,
      label: 'Long-Term',
      description: '6-12 months',
      tooltip: 'Long-term options with minimal daily theta decay. Good for conviction plays.'
    },
    'leaps': {
      days_until_exp: 450,
      label: 'LEAPS',
      description: '12+ months',
      tooltip: 'Long-term equity anticipation securities. Minimal theta decay, stock replacement strategy.'
    }
  },

  // Strike Positioning presets
  strike: {
    'deep-itm': {
      strike_pct: -0.50,
      label: 'Deep ITM',
      description: '-50%',
      tooltip: 'High intrinsic value, stock replacement. Higher cost but higher probability of profit.'
    },
    'itm': {
      strike_pct: -0.20,
      label: 'ITM',
      description: '-20%',
      tooltip: 'In the money with good delta. Higher probability, moderate leverage.'
    },
    'slight-itm': {
      strike_pct: -0.10,
      label: 'Slight ITM',
      description: '-10%',
      tooltip: 'Slightly in the money. Higher probability of profit with moderate cost.'
    },
    'atm': {
      strike_pct: 0.00,
      label: 'ATM',
      description: 'At the money',
      tooltip: 'At the money strike. Balanced risk/reward with ~50 delta.'
    },
    'near-otm': {
      strike_pct: 0.10,
      label: 'Near OTM',
      description: '+10%',
      tooltip: 'Just out of the money. Affordable entry with reasonable probability of profit.'
    },
    'slight-otm': {
      strike_pct: 0.20,
      label: 'Slight OTM',
      description: '+20%',
      tooltip: 'Slightly out of the money. Moderate leverage with reasonable probability.'
    },
    'otm': {
      strike_pct: 0.35,
      label: 'OTM',
      description: '+35%',
      tooltip: 'Out of the money. Higher leverage, lower probability of profit.'
    },
    'far-otm': {
      strike_pct: 0.50,
      label: 'Far OTM',
      description: '+50%',
      tooltip: 'Far out of the money. High risk, high reward. Needs significant move.'
    },
    'lottery': {
      strike_pct: 1.00,
      label: 'Lottery',
      description: '+100%',
      tooltip: 'Extreme OTM speculation. Very low cost, very low probability. Lottery ticket.'
    },
    'min-available': {
      strike_pct: -0.95,
      is_min_available: true,
      label: 'Min Available',
      description: 'Exchange min',
      tooltip: 'Lowest available strike on the exchange. Maximum intrinsic value.'
    },
    'max-available': {
      strike_pct: 1.00,
      is_max_available: true,
      label: 'Max Available',
      description: 'Exchange max',
      tooltip: 'Highest available strike on the exchange. Maximum speculation.'
    }
  },

  // Exit Speed presets (Days to Gain as % of Expiration)
  targetSpeed: {
    'very-fast': {
      percent: 0.20,
      label: 'Very Fast',
      description: '20% of expiration',
      tooltip: 'Quick momentum play. Exit early before theta accelerates.'
    },
    'fast': {
      percent: 0.35,
      label: 'Fast',
      description: '35% of expiration',
      tooltip: 'Early move expected. Capture gains before significant time decay.'
    },
    'balanced': {
      percent: 0.50,
      label: 'Balanced',
      description: '50% of expiration',
      tooltip: 'Mid-cycle move. Balanced approach between time and theta decay.'
    },
    'patient': {
      percent: 0.75,
      label: 'Patient',
      description: '75% of expiration',
      tooltip: 'Late expiration thesis. Willing to hold through volatility.'
    },
    'hold-to-expiry': {
      percent: 0.90,
      label: 'Hold to Expiry',
      description: '90% of expiration',
      tooltip: 'Long-term conviction. Hold almost to expiration for maximum theta collection.'
    }
  },

  // Stock Move Scenario presets
  stockMove: {
    'crash': {
      stock_gain_pct: -0.50,
      label: 'Crash',
      description: '-50%',
      sentiment: 'bearish',
      tooltip: 'Market or company collapse scenario. Severe downside.'
    },
    'major-pullback': {
      stock_gain_pct: -0.35,
      label: 'Major Pullback',
      description: '-35%',
      sentiment: 'bearish',
      tooltip: 'Severe correction or bear market. Significant downside move.'
    },
    'correction': {
      stock_gain_pct: -0.20,
      label: 'Correction',
      description: '-20%',
      sentiment: 'bearish',
      tooltip: 'Standard market correction. Typical bearish move.'
    },
    'minor-pullback': {
      stock_gain_pct: -0.10,
      label: 'Minor Pullback',
      description: '-10%',
      sentiment: 'bearish',
      tooltip: 'Normal retracement. Small pullback within trend.'
    },
    'small-rally': {
      stock_gain_pct: 0.10,
      label: 'Small Rally',
      description: '+10%',
      sentiment: 'bullish',
      tooltip: 'Mild upside. Conservative bullish estimate.'
    },
    'rally': {
      stock_gain_pct: 0.20,
      label: 'Rally',
      description: '+20%',
      sentiment: 'bullish',
      tooltip: 'Strong bullish move. Good upside momentum.'
    },
    'breakout': {
      stock_gain_pct: 0.35,
      label: 'Breakout',
      description: '+35%',
      sentiment: 'bullish',
      tooltip: 'Momentum expansion. Breaking through resistance.'
    },
    'parabolic': {
      stock_gain_pct: 0.50,
      label: 'Parabolic',
      description: '+50%',
      sentiment: 'bullish',
      tooltip: 'Extreme bullish. Parabolic price action.'
    },
    'moonshot': {
      stock_gain_pct: 1.00,
      label: 'Moonshot',
      description: '+100%',
      sentiment: 'bullish',
      tooltip: 'Stock doubles. Multi-bagger scenario.'
    },
    'supercycle': {
      stock_gain_pct: 2.00,
      label: 'Supercycle',
      description: '+200%',
      sentiment: 'bullish',
      tooltip: 'Stock triples. Exceptional growth scenario.'
    },
    'outlier': {
      stock_gain_pct: 10.00,
      label: 'Outlier',
      description: '+1000%',
      sentiment: 'bullish',
      tooltip: 'Black-swan upside event. Extremely rare 10x scenario.'
    }
  }
};

// Legacy intermediate presets (kept for backward compatibility)
export const legacyIntermediatePresets = {
  strikePositions: {
    'super-deep-itm': { strike_pct: -0.95, label: 'Super Deep ITM (Lowest Strike)' },
    'deep-itm': { strike_pct: -0.5, label: 'Deep ITM (50%)' },
    'itm': { strike_pct: -0.2, label: 'In The Money (80%)' },
    'atm': { strike_pct: 0.0, label: 'At The Money (Current)' },
    'otm': { strike_pct: 0.2, label: 'Out of Money (120%)' },
    'deep-otm': { strike_pct: 0.5, label: 'Deep OTM (150%)' },
    'super-deep-otm': { strike_pct: 1.0, label: 'Super Deep OTM (200%)' }
  },

  timeHorizons: {
    'super-short': { days_until_exp: 30, days_to_gain: 14, label: 'Super Short (30d exp / 14d hold)' },
    'short': { days_until_exp: 90, days_to_gain: 40, label: 'Short Term (90d exp / 40d hold)' },
    'medium': { days_until_exp: 270, days_to_gain: 90, label: 'Medium Term (270d exp / 90d hold)' },
    'medium-short': { days_until_exp: 270, days_to_gain: 40, label: 'Medium Short (270d exp / 40d hold)' },
    'long': { days_until_exp: 545, days_to_gain: 180, label: 'Long Term (545d exp / 180d hold)' },
    'long-medium': { days_until_exp: 545, days_to_gain: 90, label: 'Long Medium (545d exp / 90d hold)' },
    'long-short': { days_until_exp: 545, days_to_gain: 40, label: 'Long Short (545d exp / 40d hold)' }
  },

  stockMovements: {
    'pm5': { stock_gain_pct: 0.05, label: '±5%' },
    'pm10': { stock_gain_pct: 0.10, label: '±10%' },
    'pm20': { stock_gain_pct: 0.20, label: '±20%' },
    'pm35': { stock_gain_pct: 0.35, label: '±35%' },
    'pm50': { stock_gain_pct: 0.50, label: '±50%' },
    'p100': { stock_gain_pct: 1.00, label: '+100%' },
    'p200': { stock_gain_pct: 2.00, label: '+200%' }
  }
};

// Helper functions
export const createBasicScenario = (strategyKey, tickers, direction = 'bullish', movementKey = 'p10') => {
  const strategy = basicStrategies[strategyKey];
  if (!strategy) {
    throw new Error(`Unknown strategy: ${strategyKey}`);
  }

  const movement = stockMovements[movementKey];
  if (!movement) {
    throw new Error(`Unknown movement: ${movementKey}`);
  }

  return {
    ...strategy,
    tickers: Array.isArray(tickers) ? tickers.join(', ') : tickers,
    option_type: direction === 'bullish' ? 'call' : 'put',
    stock_gain_pct: movement.value, // Override with selected movement (can be positive or negative)
    label: `${strategy.label} ${movement.label}` // Combine labels
  };
};

export const createIntermediateScenario = (selections, tickers, direction = 'bullish') => {
  const { strikePosition, timeHorizon, stockMovement } = selections;

  // Support both new and legacy preset structure
  const strike = intermediatePresets.strike?.[strikePosition] ||
                 legacyIntermediatePresets.strikePositions?.[strikePosition];
  const time = intermediatePresets.expiration?.[timeHorizon] ||
               legacyIntermediatePresets.timeHorizons?.[timeHorizon];
  const movement = intermediatePresets.stockMove?.[stockMovement] ||
                   legacyIntermediatePresets.stockMovements?.[stockMovement];

  if (!strike || !time || !movement) {
    throw new Error('Invalid intermediate selections');
  }

  // For legacy presets, days_to_gain is stored directly; for new presets, use percent
  const days_to_gain = time.days_to_gain || Math.round(time.days_until_exp * 0.50);

  return {
    tickers: Array.isArray(tickers) ? tickers.join(', ') : tickers,
    option_type: direction === 'bullish' ? 'call' : 'put',
    days_until_exp: time.days_until_exp,
    strike_pct: strike.strike_pct,
    days_to_gain: days_to_gain,
    stock_gain_pct: movement.stock_gain_pct,
    allocation: 1000,
    label: `${direction === 'bullish' ? 'Call' : 'Put'} ${strike.label.split(' ')[0]} ${time.label.split(' ')[0]} ${movement.label}`
  };
};

/**
 * Parse direction string into position_type and option_type
 * @param {string} direction - 'buy_call', 'sell_put', or legacy 'call'/'put'
 * @returns {Object} { position_type: 'buy'|'sell', option_type: 'call'|'put' }
 */
export const parseDirection = (direction) => {
  // Handle new format: 'buy_call', 'buy_put', 'sell_call', 'sell_put'
  if (direction.includes('_')) {
    const [position, type] = direction.split('_');
    return {
      position_type: position,  // 'buy' or 'sell'
      option_type: type         // 'call' or 'put'
    };
  }
  // Legacy format: 'call' or 'put' defaults to 'buy'
  return {
    position_type: 'buy',
    option_type: direction
  };
};

/**
 * Build all combinations of intermediate selections into scenarios
 * @param {Object} selections - { expirations: [], strikes: [], speeds: [], moves: [] }
 * @param {Array} tickers - Array of ticker strings or objects
 * @param {string} direction - 'buy_call', 'sell_put', etc. (or legacy 'call'/'put')
 * @returns {Array} Array of scenario objects ready for API
 */
export const buildIntermediateScenarios = (selections, tickers, direction = 'buy_call') => {
  const { expirations, strikes, speeds, moves } = selections;
  const tickerValues = formatTickersForAPI(tickers);
  const scenarios = [];

  // Parse direction into position_type and option_type
  const { position_type, option_type } = parseDirection(direction);

  // Generate all combinations
  for (const expKey of expirations) {
    const exp = intermediatePresets.expiration[expKey];
    if (!exp) continue;

    for (const strikeKey of strikes) {
      const strike = intermediatePresets.strike[strikeKey];
      if (!strike) continue;

      for (const speedKey of speeds) {
        const speed = intermediatePresets.targetSpeed[speedKey];
        if (!speed) continue;

        for (const moveKey of moves) {
          const move = intermediatePresets.stockMove[moveKey];
          if (!move) continue;

          // Calculate days_to_gain from expiration and speed percentage
          const days_to_gain = Math.round(exp.days_until_exp * speed.percent);

          // Adjust strike_pct for puts (ITM/OTM reverses)
          // For calls: ITM = strike below current (-%), OTM = strike above current (+%)
          // For puts: ITM = strike above current (+%), OTM = strike below current (-%)
          const isPut = option_type === 'put';
          const adjustedStrikePct = isPut && strike.strike_pct !== 0
            ? -strike.strike_pct
            : strike.strike_pct;

          // Stock gain is LITERAL - no inversion based on option type
          // "Rally +20%" means stock UP 20% for ALL directions
          // User understands what this means for their position type
          const adjustedStockGainPct = move.stock_gain_pct;

          // Build descriptive label with position type
          const posLabel = position_type === 'buy' ? 'Buy' : 'Sell';
          const typeLabel = option_type === 'call' ? 'Calls' : 'Puts';
          const label = `${tickerValues.join(',')} ${posLabel} ${typeLabel} - ${exp.label} ${strike.label} ${speed.label} ${move.label}`;

          const scenarioObj = {
            tickers: tickerValues,
            option_type: option_type,
            position_type: position_type,  // 'buy' or 'sell'
            days_until_exp: exp.days_until_exp,
            strike_pct: adjustedStrikePct,
            days_to_gain: days_to_gain,
            stock_gain_pct: adjustedStockGainPct,
            allocation: 1000,
            label: label,
            _exitPercent: speed.percent,  // Original exit % to avoid double-rounding
            // Store original keys for loading back into form
            _intermediate: {
              expiration: expKey,
              strike: strikeKey,
              speed: speedKey,
              move: moveKey
            }
          };
          if (strike.is_min_available) scenarioObj.is_min_available = true;
          if (strike.is_max_available) scenarioObj.is_max_available = true;
          scenarios.push(scenarioObj);
        }
      }
    }
  }

  return scenarios;
};

// Map strike_pct values to human-readable labels for filter display
// For puts, ITM/OTM is inverted: positive strike_pct = ITM, negative = OTM
export const getStrikePositionLabel = (strikePct, optionType = 'call') => {
  const knownValues = {
    '-0.95': 'Min Available (-95%)',
    '-0.5': 'Deep ITM (-50%)',
    '-0.25': 'ITM (-25%)',
    '-0.2': 'ITM (-20%)',
    '-0.1': 'Slight ITM (-10%)',
    '0': 'ATM (0%)',
    '0.1': 'Near OTM (+10%)',
    '0.2': 'Slight OTM (+20%)',
    '0.25': 'OTM (+25%)',
    '0.35': 'OTM (+35%)',
    '0.5': 'Far OTM (+50%)',
    '1': 'Lottery (+100%)',
  };

  const key = String(parseFloat(strikePct));
  let label = knownValues[key];

  if (!label) {
    const pct = parseFloat(strikePct);
    const sign = pct > 0 ? '+' : '';
    const pctStr = `${sign}${(pct * 100).toFixed(0)}%`;
    if (pct < 0) label = `ITM (${pctStr})`;
    else if (pct === 0) label = 'ATM (0%)';
    else label = `OTM (${pctStr})`;
  }

  // For puts, swap ITM↔OTM (strike_pct has already been flipped, so positive = ITM for puts)
  if (optionType === 'put' && label) {
    label = label
      .replace(/\bDeep ITM\b/, '__DEEP_OTM__')
      .replace(/\bFar OTM\b/, '__FAR_ITM__')
      .replace(/\bSlight OTM\b/, '__SLIGHT_ITM__')
      .replace(/\bITM\b/, '__OTM__')
      .replace(/\bOTM\b/, '__ITM__')
      .replace('__DEEP_OTM__', 'Deep OTM')
      .replace('__FAR_ITM__', 'Far ITM')
      .replace('__SLIGHT_ITM__', 'Slight ITM')
      .replace('__OTM__', 'OTM')
      .replace('__ITM__', 'ITM');
  }

  return label;
};

/**
 * Simplified strike position label for column display and filter dropdowns.
 * Returns only "ITM (-X%)", "OTM (+X%)", or "ATM (0%)" — no verbose prefixes
 * like "Deep", "Slight", "Far", "Lottery", etc.
 * Sign convention: ITM always shown as negative, OTM always shown as positive.
 */
export const getSimpleStrikePositionLabel = (strikePct, optionType = 'call') => {
  const pct = parseFloat(strikePct);
  if (isNaN(pct)) return '-';

  const absPct = Math.abs(pct * 100);
  const rounded = Math.round(absPct);

  if (pct === 0) return 'ATM (0%)';

  // For calls: positive strike_pct = OTM, negative = ITM
  // For puts: inverted — positive strike_pct = ITM, negative = OTM
  const isPut = optionType?.toLowerCase() === 'put';
  const isOTM = isPut ? pct < 0 : pct > 0;

  if (isOTM) {
    return `OTM (+${rounded}%)`;
  } else {
    return `ITM (-${rounded}%)`;
  }
};

// Map days_until_exp to human-readable expiration range (matches strategy card format)
export const getExpirationRangeLabel = (daysUntilExp) => {
  if (daysUntilExp < 30) return '< 1 month';
  if (daysUntilExp < 100) return '1-3 months';
  if (daysUntilExp < 180) return '3-6 months';
  if (daysUntilExp < 365) return '6-12 months';
  return '12+ months';
};

// Calculate days-to-exit as percentage of expiration (matches strategy card format)
// If originalPercent (0-1) is provided, use it directly to avoid double-rounding
export const getDaysToExitPercent = (daysToGain, daysUntilExp, originalPercent = null) => {
  if (originalPercent != null) return `${Math.round(originalPercent * 100)}%`;
  if (!daysUntilExp || daysUntilExp === 0) return '0%';
  return `${Math.round((daysToGain / daysUntilExp) * 100)}%`;
};

// Convert ticker objects to strings for API
export const formatTickersForAPI = (tickers) => {
  if (Array.isArray(tickers)) {
    return tickers.map(t => typeof t === 'object' ? t.value : t);
  }
  return tickers;
};